'use client'

import { memo, useCallback, useMemo, useRef, useEffect, useState } from 'react'

import { v4 as uuidv4 } from 'uuid'

import { useInfiniteScroll } from '@/features/v2/casino/hooks/useIntersectionObserver'
import { GameGridProps } from '@/features/v2/casino/interfaces/component.interface'
import { smoothScrollToElement } from '@/features/v2/casino/utils/scrollUtils'

import GameCardComponent from './GameCard'
import LoadingSkeleton from './LoadingSkeleton'
import { core } from '../localization/core'

function GameGrid({
  games,
  loading,
  hasMore,
  onLoadMore,
}: Readonly<GameGridProps>): React.ReactElement {
  const gridRef = useRef<HTMLDivElement>(null)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [previousGamesLength, setPreviousGamesLength] = useState(0)

  const handleLoadMore = useCallback(() => {
    if (loading || !hasMore) {
      return
    }
    onLoadMore()
  }, [loading, hasMore, onLoadMore])

  const loadMoreRef = useInfiniteScroll(handleLoadMore, hasMore, loading)

  const isReset = games.length < previousGamesLength && previousGamesLength > 0

  useEffect(() => {
    if (gridRef.current && !loading && games.length > 0) {
      if (isInitialLoad || isReset) {
        smoothScrollToElement('[data-game-grid]')
        setIsInitialLoad(false)
      }
    }
  }, [games.length, loading, isInitialLoad, isReset])

  useEffect(() => {
    setPreviousGamesLength(games.length)
  }, [games.length])

  const gameList = useMemo(() => {
    return games.map((game, gameIndex) => {
      return <GameCardComponent key={uuidv4()} game={game} index={gameIndex} />
    })
  }, [games])

  if (loading && games.length === 0) {
    return <LoadingSkeleton count={12} />
  }

  if (games.length === 0 && !loading) {
    return (
      <div className='text-center py-12'>
        <div className='text-default-500 text-lg mb-2'>{core.grid.noGames}</div>
        <p className='text-default-400 text-sm'>{core.grid.tryFilters}</p>
      </div>
    )
  }

  return (
    <div ref={gridRef} data-game-grid className='space-y-2 w-full mx-auto'>
      <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-2 md:gap-2 lg:gap-3'>
        {gameList}
      </div>

      {loading && games.length > 0 && (
        <div className='flex justify-center py-4'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-hprimary-500' />
        </div>
      )}

      {hasMore && !loading && games.length > 0 && (
        <div ref={loadMoreRef} className='h-4' />
      )}

      {!hasMore && games.length > 0 && (
        <div className='text-center py-4'>
          <p className='text-default-500 text-sm'>{core.grid.allGames}</p>
        </div>
      )}
    </div>
  )
}

export default memo(GameGrid)
